import React from 'react'
import {
  GridColDef,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridRenderCellParams,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Link } from '@mui/material'
import { AbsenceDay } from '../../../types/absence-day'
import Paragraph from '../../../shared/UI/Paragraph'
import ActionIcon from '../../../shared/UI/ActionIcon'
import { RowExpansionToggle } from '../../MyActionsGrid/RowExpansionToggle'
import {
  formatAbsenceHours,
  getPayApprovalHoursDisplayValue,
  getUnpaidHoursDisplayValue,
  isBereavementOrFuneral,
} from '../../../utils/absence-utils'
import { AbsenceStatusType } from '../../../utils/constants'
import { AbsenceReasonTypesEnum } from '../../Absence/types'

interface LegacyColumnDefinitionParams {
  hasManAuthRequest: boolean
  isSickness: boolean
  onEdit?: (row: AbsenceDay) => void
  onDelete: (row: AbsenceDay) => void
  headerText: (testId: string, headerName?: string) => React.ReactNode
  getColumnTestId: (columnName: string) => string
  userPermissions: string[]
  viewOnly?: boolean
  handleViewClick?: (row: AbsenceDay) => void
  isPayrollApproverReadOnlyView?: boolean
  isRtwComplete?: boolean
  rtwRequired?: boolean
  absenceStatusType?: string | null
  absenceTypeId?: number
}

const sicknessOnlyColumns = ['sickNoteAdded', 'ospUsed']

export function createLegacyAbsenceDetailColumns(
  options: LegacyColumnDefinitionParams
): GridColDef[] {
  const {
    hasManAuthRequest,
    isSickness,
    onEdit,
    onDelete,
    headerText,
    getColumnTestId,
    userPermissions,
    viewOnly = false,
    handleViewClick,
    isPayrollApproverReadOnlyView = false,
    isRtwComplete,
    rtwRequired = true,
    absenceStatusType,
    absenceTypeId,
  } = options

  const columns: GridColDef[] = []
  const showToggle = !hasManAuthRequest && isSickness

  const isAwaitingSca = absenceStatusType === AbsenceStatusType.AWAITING_SCA
  const isEditDisabled =
    isPayrollApproverReadOnlyView || isRtwComplete || (!rtwRequired && !isAwaitingSca)
  const isDeleteDisabled =
    isPayrollApproverReadOnlyView || isRtwComplete || (!rtwRequired && !isAwaitingSca)

  const isBereavementOrFuneralAbsence = isBereavementOrFuneral(absenceTypeId)

  const unpaidHeaderName =
    {
      [AbsenceReasonTypesEnum.Bereavement]: 'Bereavement Hours',
      [AbsenceReasonTypesEnum.FuneralAttendance]: 'Funeral Hours',
    }[absenceTypeId as number] || 'Unpaid'

  if (showToggle) {
    columns.push({
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 60,
      minWidth: 60,
      renderCell: cellParams => (
        <div
          data-testid={`Absence-Detail-Table-Toggle-${cellParams.id}`}
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        >
          <RowExpansionToggle id={cellParams.id} value={cellParams.value} />
        </div>
      ),
    })
  }

  columns.push(
    {
      field: 'date',
      headerName: 'Date',
      headerClassName: 'header',
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
      renderCell: params => (
        <Paragraph>
          {params.value ? new Date(params.value).toLocaleDateString(navigator.language) : 'N/A'}
        </Paragraph>
      ),
    },
    {
      field: 'sickNoteAdded',
      headerName: 'Sick Note Added',
      headerClassName: 'header',
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
      renderCell: params => <Paragraph>{params.value ? 'Yes' : 'No'}</Paragraph>,
    },
    {
      field: 'toWorkBack',
      headerName: 'To Work Back',
      headerClassName: 'header',
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
      renderCell: params => <Paragraph>{params.value ? 'Yes' : 'No'}</Paragraph>,
    },
    {
      field: 'ospUsed',
      headerName: 'OSP Used',
      headerClassName: 'header',
      flex: 0.75,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: params => headerText(getColumnTestId(params.field), params.colDef.headerName),
      renderCell: params => <Paragraph>{formatAbsenceHours(params.value)}</Paragraph>,
    },
    ...(!isBereavementOrFuneralAbsence
      ? [
          {
            field: 'payApproval',
            headerName: 'Discretionary Pay',
            headerClassName: 'header',
            flex: 0.75,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: (params: GridColumnHeaderParams) =>
              headerText(getColumnTestId(params.field), params.colDef.headerName),
            renderCell: (params: GridRenderCellParams) => {
              const { manAuthRequestStatus } = params.row
              return (
                <Paragraph>
                  {getPayApprovalHoursDisplayValue(params.value, manAuthRequestStatus)}
                </Paragraph>
              )
            },
          },
        ]
      : []),
    {
      field: 'unpaid',
      headerName: unpaidHeaderName,
      headerClassName: 'header',
      flex: 0.75,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: GridColumnHeaderParams) =>
        headerText(getColumnTestId(params.field), params.colDef.headerName),
      renderCell: (params: GridRenderCellParams) => (
        <Paragraph>
          {getUnpaidHoursDisplayValue(
            params.value,
            params.row.payApproval,
            params.row.manAuthRequestStatus
          )}
        </Paragraph>
      ),
    },
    ...(viewOnly && handleViewClick
      ? [
          {
            field: 'view',
            headerName: '',
            width: 20,
            minWidth: 20,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 0.25,
            renderCell: (cellParams: GridRenderCellParams<AbsenceDay>) => (
              <ActionIcon onClick={() => handleViewClick(cellParams.row)}>
                <Link href="#" variant="body2">
                  View
                </Link>
              </ActionIcon>
            ),
          },
        ]
      : [
          {
            field: 'edit',
            headerName: '',
            width: 20,
            minWidth: 20,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 0.25,
            renderCell: (cellParams: GridRenderCellParams<AbsenceDay>) => (
              <ActionIcon disabled={isEditDisabled}>
                <EditIcon
                  color={isPayrollApproverReadOnlyView ? undefined : 'primary'}
                  onClick={() => onEdit?.(cellParams.row)}
                />
              </ActionIcon>
            ),
          },
          ...(userPermissions.includes('DeleteAttendance')
            ? [
                {
                  field: 'delete',
                  headerName: '',
                  width: 20,
                  minWidth: 20,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  flex: 0.25,
                  renderCell: (cellParams: GridRenderCellParams<AbsenceDay>) => (
                    <ActionIcon disabled={isDeleteDisabled}>
                      <DeleteIcon
                        color={isPayrollApproverReadOnlyView ? undefined : 'primary'}
                        onClick={() => onDelete(cellParams.row)}
                      />
                    </ActionIcon>
                  ),
                },
              ]
            : []),
        ])
  )

  return !isSickness
    ? columns.filter(column => !sicknessOnlyColumns.includes(column.field))
    : columns
}
